
import { Navigation } from "@/components/Navigation";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { InvoiceHeader } from "@/components/invoices/InvoiceHeader";
import { MonthSelector } from "@/components/invoices/MonthSelector";
import { ProjectSelector } from "@/components/invoices/ProjectSelector";
import { SummaryCards } from "@/components/invoices/SummaryCards";
import { InvoicePreviewCard } from "@/components/invoices/InvoicePreviewCard";
import { InvoiceSettingsCard } from "@/components/invoices/InvoiceSettingsCard";

export default function Invoices() {
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [selectedProject, setSelectedProject] = useState<string>("all");
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: entries = [] } = useQuery({
    queryKey: ['time-entries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('time_entries')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;
      return data.map(entry => ({
        id: entry.id,
        date: new Date(entry.date),
        minutes: entry.minutes,
        projectId: entry.project_id,
        description: entry.description
      }));
    }
  });

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('is_archived', false);

      if (error) throw error;
      return data.map(project => {
        // Handle potential JSON parsing safely
        let pricingComponents;
        let invoiceSettings;
        
        try {
          if (project.pricing_components) {
            pricingComponents = typeof project.pricing_components === 'string' 
              ? JSON.parse(project.pricing_components) 
              : project.pricing_components;
          }
          
          if (project.invoice_settings) {
            invoiceSettings = typeof project.invoice_settings === 'string'
              ? JSON.parse(project.invoice_settings)
              : project.invoice_settings;
          }
        } catch (e) {
          console.error("Error parsing project data:", e);
        }

        return {
          id: project.id,
          name: project.name,
          description: project.description || undefined,
          isArchived: project.is_archived,
          createdAt: new Date(project.created_at),
          hourlyRate: Number(project.hourly_rate),
          pricingComponents: pricingComponents,
          invoiceSettings: invoiceSettings,
          user_id: project.user_id,
          team_id: project.team_id,
          owner_id: project.owner_id,
          is_team_project: project.is_team_project || false,
        };
      });
    }
  });

  // Filter projects based on selected project
  const filteredEntries = selectedProject === "all" 
    ? entries 
    : entries.filter(entry => entry.projectId === selectedProject);

  // Calculate total statistics for summary cards
  const totalMinutes = filteredEntries.reduce((sum, entry) => {
    const entryMonth = new Date(entry.date).getMonth();
    const entryYear = new Date(entry.date).getFullYear();
    const selectedMonthValue = selectedMonth.getMonth();
    const selectedYearValue = selectedMonth.getFullYear();
    
    if (entryMonth === selectedMonthValue && entryYear === selectedYearValue) {
      return sum + entry.minutes;
    }
    return sum;
  }, 0);
  
  const totalHours = totalMinutes / 60;
  
  // Calculate revenue based on project rates
  const totalRevenue = filteredEntries.reduce((sum, entry) => {
    const entryMonth = new Date(entry.date).getMonth();
    const entryYear = new Date(entry.date).getFullYear();
    const selectedMonthValue = selectedMonth.getMonth();
    const selectedYearValue = selectedMonth.getFullYear();
    
    if (entryMonth === selectedMonthValue && entryYear === selectedYearValue) {
      const project = projects.find(p => p.id === entry.projectId);
      const hourlyRate = project ? project.hourlyRate : 0;
      return sum + ((entry.minutes / 60) * hourlyRate);
    }
    return sum;
  }, 0);

  // Get client info from selected project
  const selectedProjectData = selectedProject !== "all" 
    ? projects.find(p => p.id === selectedProject) 
    : null;

  const handleInvoiceSettings = () => {
    if (selectedProject === "all") {
      toast({
        title: "Select a project first",
        description: "Please select a specific project to edit its invoice settings.",
      });
      return;
    }

    // Navigate to the project detail page with invoice settings tab open
    navigate(`/projects/${selectedProject}?tab=invoice`);
  };

  return (
    <>
      <Navigation />
      <div className="container py-8 space-y-8 mt-14">
        <InvoiceHeader>
          <div className="flex flex-wrap items-center gap-2">
            <ProjectSelector 
              projects={projects}
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
            />
            <MonthSelector 
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
            />
          </div>
        </InvoiceHeader>

        <SummaryCards 
          totalHours={totalHours}
          totalRevenue={totalRevenue}
          selectedProject={selectedProject}
          selectedMonth={selectedMonth}
          selectedProjectData={selectedProjectData}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <InvoicePreviewCard 
              handleInvoiceSettings={handleInvoiceSettings}
              filteredEntries={filteredEntries}
              projects={projects}
              selectedMonth={selectedMonth}
            />
          </div>
          
          <div className="lg:col-span-1">
            <InvoiceSettingsCard />
          </div>
        </div>
      </div>
    </>
  );
}
